import { forwardRef, useCallback } from 'react';
import cn from 'clsx';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useMapStore } from '@pkgs/components/hooks/stores/useMapStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { PRIVATE_OFFICE_DEFAULT_MAP_BOUNDING_BOX } from '@pkgs/components/consts/map';
import { DEFAULT_IMAGE, getUploadCareImageUrl, type AgentResponse } from '@ev-tech/search-modules-api';

import { Chip } from '../Chip/Chip';
import { getUploadcareImageTransformationProps } from '../../utils/getUploadcareImageTransformationProps';

import styles from './AgentFilterChip.module.css';

type Props = {
  className?: string;
  'data-testid'?: string;
  agentProfile: AgentResponse;
};

export const AgentFilterChip = forwardRef<HTMLDivElement, Props>(({ className, 'data-testid': dataTestId, agentProfile }, ref) => {
  const setPromotedBy = useSearchStore((state) => state.setPromotedBy);
  const setAgentId = useSearchStore((state) => state.setAgentId);
  const setPage = useSearchStore((state) => state.setPage);
  const updateMapBounds = useMapStore((state) => state.updateMapBounds);
  const {
    ImageComponent,
    options: { language },
  } = useAppContext();

  const handleOnDelete = useCallback(() => {
    setPromotedBy(undefined);
    setAgentId(undefined);
    setPage(1);

    updateMapBounds(PRIVATE_OFFICE_DEFAULT_MAP_BOUNDING_BOX[language] ?? PRIVATE_OFFICE_DEFAULT_MAP_BOUNDING_BOX.others);
  }, [setPromotedBy, setAgentId, setPage, updateMapBounds, language]);

  const agentName = `${agentProfile.firstName} ${agentProfile.lastName}`;
  const agentPhotoSource = agentProfile.profilePictureUploadcareId
    ? getUploadCareImageUrl({ imageId: agentProfile.profilePictureUploadcareId })
    : agentProfile.profilePicture;

  return (
    <div ref={ref} className={cn(styles.container, className)}>
      <Chip
        className={styles.chip}
        icon={
          <ImageComponent
            className={styles.agentPhoto}
            src={agentPhotoSource ?? DEFAULT_IMAGE}
            alt={`${agentName} profile photo`}
            width={40}
            height={40}
            {...getUploadcareImageTransformationProps({ aspectRatio: '1/1', fit: 'cover' })}
          />
        }
        label={agentName}
        onDelete={handleOnDelete}
        title={agentName}
        data-testid={dataTestId ?? 'agent-filter-chip'}
      />
    </div>
  );
});

AgentFilterChip.displayName = 'AgentFilterChip';
